'use server'

import { apiRequest, ApiResponse } from '../request'
import { getClient } from '../client'
import { downloadPdf } from '../../download-blob'

export type CatalogCategory = 'EXAM' | 'PROCEDURE' | 'CONSULTATION' | 'TELECONSULTATION' | 'PACKAGE' | 'PRODUCT'

export const CATALOG_CATEGORY_LABELS: Record<CatalogCategory, string> = {
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
    CONSULTATION: 'Consulta',
    TELECONSULTATION: 'Teleconsulta',
    PACKAGE: 'Pacote',
    // Mercadoria: o rótulo NÃO muda por segmento — "Produto" é produto na clínica e no
    // salão. Quem varia é Procedimento/Serviço (ver useServiceLabel).
    PRODUCT: 'Produto',
}

export interface CatalogPartner {
    id: number
    catalogId: number
    partnerId: number
    partner: {
        id: number
        name: string
        tradingName?: string
        city?: string | null
        state?: string | null
    }
    price?: number | null
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
    observations?: string | null
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface CatalogProfessional {
    id: number
    catalogId: number
    professionalId: number
    professional: {
        id: number
        name: string
        registrationNumber?: string
        councilName?: string
    }
    price?: number | null
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface CatalogCompanyPrice {
    companyId: number
    price: number
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
    /** Produto: preço promocional da filial, em centavos, com o período. */
    promotionalPrice?: number | null
    promotionalStart?: string | null
    promotionalEnd?: string | null
}

// === VARIAÇÕES ===
export interface CatalogVariationCompanyPrice {
    companyId: number
    price: number
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
    active?: boolean
}

export interface CatalogVariation {
    id: number
    catalogId: number
    name: string
    tussCode?: string | null
    active: boolean
    sortOrder: number
    companyPrices?: CatalogVariationCompanyPrice[]
    /**
     * A variação é um Catalog filho e tem parceiros/profissionais PRÓPRIOS, com preço
     * próprio. O backend resolve o preço pela cascata parceiro > profissional > empresa
     * (`resolveVariationsForPricing`) — o front DEVE espelhar isso, senão exibe o preço
     * da empresa enquanto o backend cobra o do parceiro.
     */
    partners?: CatalogPartner[]
    professionals?: CatalogProfessional[]
    createdAt: string
    updatedAt: string
}

export interface CreateCatalogVariationData {
    name: string
    tussCode?: string
    active?: boolean
    sortOrder?: number
    // Empresa (filial) cujo preço da variação está sendo gravado.
    companyId: number
    price?: number
    sellingPrice?: number
    markupPercentage?: number
    maxDiscount?: number
}

export interface UpdateCatalogVariationData extends Partial<CreateCatalogVariationData> {}

/**
 * Preço-base por empresa (preço por filial). NÃO há fallback para um preço
 * único do item — vem só de companyPrices da empresa informada (a selecionada).
 */
export interface ResolvedCatalogPrice {
    price: number
    sellingPrice?: number
    markupPercentage?: number
    maxDiscount?: number
}

export function catalogCompanyPrice(
    catalog: { companyPrices?: CatalogCompanyPrice[] | null },
    companyId?: number | null,
): ResolvedCatalogPrice {
    const list = catalog.companyPrices || []
    const cp =
        list.find(p => p.companyId === companyId) ??
        (companyId == null ? list[0] : undefined)
    // Campos opcionais como number | undefined (não null) — casam com as funções
    // de formatação (formatPrice/formatCents) que esperam number | undefined.
    return {
        price: cp?.price ?? 0,
        sellingPrice: cp?.sellingPrice ?? undefined,
        markupPercentage: cp?.markupPercentage ?? undefined,
        maxDiscount: cp?.maxDiscount ?? undefined,
    }
}

/** Preço-base "de venda" efetivo: sellingPrice > price > 0. */
export function catalogBasePrice(
    catalog: { companyPrices?: CatalogCompanyPrice[] | null },
    companyId?: number | null,
): number {
    const cp = catalogCompanyPrice(catalog, companyId)
    return cp.sellingPrice || cp.price || 0
}

/** Campos que só o PRODUTO tem (`category = 'PRODUCT'`), no satélite. */
export interface CatalogProductDetails {
    id?: number
    sku?: string | null
    barcode?: string | null
    brand?: string | null
    externalId?: string | null
    unit?: string | null
    tags?: string[]
    ncm?: string | null
    cest?: string | null
    cfop?: string | null
    origin?: string | null
    weight?: number | string | null
    width?: number | string | null
    height?: number | string | null
    depth?: number | string | null
    priceType?: string | null
    warranty?: string | null
    attributes?: unknown
    notes?: string | null
}

/** Estoque do produto POR FILIAL — espelha `CatalogCompanyPrice`. */
export interface CatalogCompanyStock {
    id?: number
    catalogId?: number
    companyId: number
    stockEnabled?: boolean
    stockQuantity?: number | null
    minStock?: number | null
    active?: boolean
}

export interface Catalog {
    id: number
    name: string
    category: CatalogCategory
    // ===== Produto =====
    supplierId?: number | null
    supplier?: { id: number; name: string; tradingName?: string | null } | null
    productCategoryId?: number | null
    productCategory?: { id: number; name: string } | null
    productSubcategoryId?: number | null
    productSubcategory?: { id: number; name: string } | null
    productDetails?: CatalogProductDetails | null
    companyStocks?: CatalogCompanyStock[]
    /** Galeria do item, já na ordem de exibição. Vem junto no `findOne`. */
    media?: CatalogMedia[]
    budgetOnly?: boolean
    /** Procedimento cirúrgico (só em `category: 'PROCEDURE'`). */
    isSurgical?: boolean
    tussCode?: string | null
    description?: string | null
    typeId?: number | null
    catalogType?: { id: number; name: string; category: CatalogCategory } | null
    // Preço por filial (CatalogCompanyPrice) — não há mais preço único no item.
    companyPrices?: CatalogCompanyPrice[]
    defaultDuration?: number
    /**
     * Material biológico coletado — "Sangue", "Urina", "Fezes".
     *
     * O pedido de exame laboratorial é impresso AGRUPADO por ele, porque é o material
     * que define o tubo no laboratório.
     */
    collectionMaterial?: string | null
    active: boolean
    companyId: number
    // Consultation/Teleconsultation
    specialtyId?: number | null
    specialty?: { id: number; name: string } | null
    specialties?: { specialty: { id: number; name: string } }[]
    // Exam
    instructions?: string | null
    // Order
    preInstructions?: string | null
    postInstructions?: string | null
    // Teleconsultation config
    maxTeleDuration?: number | null
    enableChat?: boolean | null
    enableScreenShare?: boolean | null
    recordingEnabled?: boolean | null
    // Order/Exam — taxa do anestesista (base por item, no Catalog)
    anesthetistFee?: number | null
    /**
     * Aparece no seletor de agendamento. Pacote novo nasce `false`: é VENDIDO pelo próprio
     * cadastro, com data por componente.
     */
    schedulable?: boolean
    /** Destinatário PADRÃO da taxa, herdado pelo agendamento. */
    anesthetistRecipientKind?: 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    anesthetistRecipientId?: number | null
    // Many-to-many
    partners: CatalogPartner[]
    professionals: CatalogProfessional[]
    // Variação-como-item (novo modelo): filhas Catalog (isVariation=true).
    // Ver docs/catalog-variation-as-item-redesign.md.
    variationChildren?: Catalog[]
    parentId?: number | null
    // Pai da variação (contexto). Preenchido pelo endpoint de itens vinculados a
    // uma entidade (`getLinkedCatalogItems`), onde variações aparecem na listagem.
    parent?: { id: number; name: string } | null
    isVariation?: boolean
    sortOrder?: number
    medicalSupplies?: CatalogMedicalSupply[]
    catalogExams?: CatalogExam[]
    // Package — componentes (quando category = PACKAGE)
    packageItems?: CatalogPackageItem[]
    hospitals?: {
        hospitalId: number
        hospital: {
            id: number
            name: string
            tradingName?: string | null
            cnpj?: string | null
            city?: string | null
            state?: string | null
        }
    }[]
    createdAt: string
    updatedAt: string
}

export interface CreateCatalogData {
    name: string
    category: CatalogCategory
    companyId: number
    /**
     * Grava o MESMO preço em todas as filiais da organização — e **substitui** o que elas
     * tiverem. É uma ação deste salvamento: filial criada depois não herda. Só preço; o
     * estoque continua sendo de cada unidade.
     */
    applyPriceToAllCompanies?: boolean
    tussCode?: string
    /** Material biológico coletado; agrupa o pedido de exame impresso. */
    collectionMaterial?: string
    description?: string
    typeId?: number
    price: number
    /** `null` limpa o preço gravado; `undefined` deixa como está. */
    sellingPrice?: number | null
    /** `null` limpa o markup gravado; `undefined` deixa como está. */
    markupPercentage?: number | null
    maxDiscount?: number
    defaultDuration?: number
    active?: boolean
    /** Apenas orçamento: o item não pode ser agendado diretamente. */
    budgetOnly?: boolean
    /** Procedimento cirúrgico. Só faz sentido em `category: 'PROCEDURE'`. */
    isSurgical?: boolean
    specialtyId?: number
    specialtyIds?: number[]
    instructions?: string
    preInstructions?: string
    postInstructions?: string
    anesthetistFee?: number
    schedulable?: boolean
    anesthetistRecipientKind?: 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    anesthetistRecipientId?: number | null
    maxTeleDuration?: number
    enableChat?: boolean
    enableScreenShare?: boolean
    recordingEnabled?: boolean
    // ===== Produto (category = 'PRODUCT') =====
    supplierId?: number
    productCategoryId?: number
    productSubcategoryId?: number
    productDetails?: CatalogProductDetails
    /** Estoque na filial de `companyId` — o item é da org, a mercadoria é da unidade. */
    stock?: {
        stockEnabled?: boolean
        stockQuantity?: number
        minStock?: number
        active?: boolean
    }
    hospitalIds?: number[]
}

export interface UpdateCatalogData extends Partial<CreateCatalogData> {}

export interface CreateCatalogPartnerData {
    partnerId: number
    price?: number
    sellingPrice?: number
    markupPercentage?: number
    maxDiscount?: number
    observations?: string
    active?: boolean
}

export interface UpdateCatalogPartnerData extends Partial<Omit<CreateCatalogPartnerData, 'partnerId'>> {}

export interface CreateCatalogProfessionalData {
    professionalId: number
    price?: number
    sellingPrice?: number
    markupPercentage?: number
    maxDiscount?: number
    active?: boolean
}

export interface UpdateCatalogProfessionalData extends Partial<Omit<CreateCatalogProfessionalData, 'professionalId'>> {}

export interface EffectivePrice {
    source: 'catalog' | 'partner' | 'professional'
    partnerId?: number
    professionalId?: number
    price?: number | null
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
}

export interface CatalogMedicalSupply {
    id: number
    catalogId: number
    medicalSupplyId: number
    medicalSupply: {
        id: number
        name: string
        type: string
        unit: string
        quantity: number
        costPrice: number
        sellingPrice?: number | null
    }
    quantity: number
    unitPrice: number
    notes?: string | null
    createdAt: string
    updatedAt: string
}

// ==================== CRUD PRINCIPAL ====================

export async function getCatalogsByCompany(
    companyId: number,
    params?: {
        activeOnly?: boolean
        /** Só o que pode ser agendado. A busca do AGENDAMENTO passa true. */
        schedulableOnly?: boolean
        // Contexto da listagem do catálogo: ignora o filtro de intermediação
        // (só itens com parceiro). NÃO enviar nos agendamentos.
        isListing?: boolean
        page?: number
        limit?: number
        search?: string
        partnerId?: number
        typeId?: number
        professionalId?: number
        specialtyId?: number
        category?: CatalogCategory
        /** Sem `category`: restringe às categorias liberadas para o usuário. */
        categories?: CatalogCategory[]
        sortBy?: string
        sortOrder?: string
        maxPrice?: number
        minPrice?: number
        exactPrice?: number
        // Quando true, traz apenas itens com código TUSS preenchido.
        hasTuss?: boolean
    }
): Promise<ApiResponse<Catalog[]>> {
    const queryParams: Record<string, any> = {}
    if (params?.activeOnly) queryParams.activeOnly = 'true'
    if (params?.schedulableOnly) queryParams.schedulableOnly = 'true'
    if (params?.isListing) queryParams.isListing = 'true'
    if (params?.page) queryParams.page = params.page
    if (params?.limit) queryParams.limit = params.limit
    if (params?.search) queryParams.search = params.search
    if (params?.partnerId) queryParams.partnerId = params.partnerId
    if (params?.typeId) queryParams.typeId = params.typeId
    if (params?.professionalId) queryParams.professionalId = params.professionalId
    if (params?.specialtyId) queryParams.specialtyId = params.specialtyId
    if (params?.category) queryParams.category = params.category
    if (params?.categories?.length)
        queryParams.categories = params.categories.join(',')
    if (params?.sortBy) queryParams.sortBy = params.sortBy
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder
    if (params?.maxPrice != null) queryParams.maxPrice = params.maxPrice
    if (params?.minPrice != null) queryParams.minPrice = params.minPrice
    if (params?.exactPrice != null) queryParams.exactPrice = params.exactPrice
    if (params?.hasTuss) queryParams.hasTuss = 'true'
    return apiRequest<Catalog[]>(`catalog/company/${companyId}`, { params: queryParams })
}

/**
 * `scheduling`: o item como o agendamento o vê, só com os parceiros da filial
 * de quem agenda. Sem ele, vêm os parceiros de todas as filiais (o editor do
 * catálogo precisa deles para desvincular).
 */
export async function getCatalog(
    id: number,
    options?: { scheduling?: boolean }
): Promise<ApiResponse<Catalog>> {
    const query = options?.scheduling ? '?scope=scheduling' : ''
    return apiRequest<Catalog>(`catalog/${id}${query}`, {})
}

/**
 * Busca um item RAIZ do catálogo por código TUSS na organização da empresa.
 * `result` vem `null` quando não existe. Usado pelo formulário para detectar
 * duplicidade de TUSS e cair em modo de edição do item existente.
 */
export async function getCatalogByTuss(
    companyId: number,
    tussCode: string
): Promise<ApiResponse<Catalog | null>> {
    return apiRequest<Catalog | null>(`catalog/company/${companyId}/by-tuss`, {
        params: { tussCode },
    })
}

export type CatalogLinkEntityType = 'partner' | 'professional' | 'hospital'

/**
 * Lista paginada dos itens de catálogo vinculados a uma entidade (parceiro,
 * profissional ou hospital). Diferente de `getCatalogsByCompany`, INCLUI as
 * variações-como-item como itens normais (com o `parent` para contexto). Cada
 * item traz apenas o vínculo da entidade consultada (`partners`/`professionals`/
 * `hospitals` com um único elemento). Usada nas abas "Catálogo do
 * Parceiro/Profissional/Hospital".
 */
export async function getLinkedCatalogItems(
    companyId: number,
    params: {
        entityType: CatalogLinkEntityType
        entityId: number
        page?: number
        limit?: number
        search?: string
    }
): Promise<ApiResponse<Catalog[]>> {
    const queryParams: Record<string, any> = {
        entityType: params.entityType,
        entityId: params.entityId,
    }
    if (params.page) queryParams.page = params.page
    if (params.limit) queryParams.limit = params.limit
    if (params.search) queryParams.search = params.search
    return apiRequest<Catalog[]>(`catalog/company/${companyId}/linked`, {
        params: queryParams,
    })
}

export async function unifyCatalogs(data: {
    targetId: number
    sourceIds: number[]
    tussCode: string
}): Promise<ApiResponse<{ targetId: number; merged: number; tussCode: string }>> {
    return apiRequest('catalog/unify', { data }, 'post')
}

export async function bulkDeleteCatalogs(
    ids: number[]
): Promise<ApiResponse<{ deleted: number }>> {
    return apiRequest('catalog/bulk-delete', { data: { ids } }, 'post')
}

export async function createCatalog(data: CreateCatalogData): Promise<ApiResponse<Catalog>> {
    return apiRequest<Catalog>('catalog', { data }, 'post')
}

export async function updateCatalog(id: number, data: UpdateCatalogData): Promise<ApiResponse<Catalog>> {
    return apiRequest<Catalog>(`catalog/${id}`, { data }, 'patch')
}

export async function toggleCatalogActive(id: number): Promise<ApiResponse<Catalog>> {
    return apiRequest<Catalog>(`catalog/${id}/toggle-active`, {}, 'patch')
}

export async function deleteCatalog(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${id}`, {}, 'delete')
}

export async function getEffectivePrice(
    id: number,
    partnerId?: number,
    professionalId?: number
): Promise<ApiResponse<EffectivePrice>> {
    const params: Record<string, any> = {}
    if (partnerId) params.partnerId = partnerId
    if (professionalId) params.professionalId = professionalId
    return apiRequest<EffectivePrice>(`catalog/${id}/effective-price`, { params })
}

// ==================== PARCEIROS ====================

export async function addCatalogPartner(
    catalogId: number,
    data: CreateCatalogPartnerData
): Promise<ApiResponse<CatalogPartner>> {
    return apiRequest<CatalogPartner>(`catalog/${catalogId}/partners`, { data }, 'post')
}

export async function getCatalogPartners(catalogId: number): Promise<ApiResponse<CatalogPartner[]>> {
    return apiRequest<CatalogPartner[]>(`catalog/${catalogId}/partners`, {})
}

export async function updateCatalogPartner(
    catalogId: number,
    partnerId: number,
    data: UpdateCatalogPartnerData
): Promise<ApiResponse<CatalogPartner>> {
    return apiRequest<CatalogPartner>(`catalog/${catalogId}/partners/${partnerId}`, { data }, 'patch')
}

export async function removeCatalogPartner(
    catalogId: number,
    partnerId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${catalogId}/partners/${partnerId}`, {}, 'delete')
}

// ==================== PRECIFICAÇÃO EM MASSA DE VARIAÇÕES ====================

export interface SetPartnerData {
    partnerId: number
    price?: number | null
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
    observations?: string | null
    active?: boolean
}

/**
 * Lista paginada dos PAIS que têm variações-filhas, cada um com suas variações e
 * os parceiros já vinculados a cada variação. Alimenta a tela
 * `/registrations/catalog/variations-pricing`.
 */
export async function getVariationsPricing(
    companyId: number,
    params?: { page?: number; limit?: number; search?: string }
): Promise<ApiResponse<Catalog[]>> {
    const queryParams: Record<string, any> = { companyId }
    if (params?.page) queryParams.page = params.page
    if (params?.limit) queryParams.limit = params.limit
    if (params?.search) queryParams.search = params.search
    return apiRequest<Catalog[]>('catalog/variations-pricing', { params: queryParams })
}

/** Define (substitui) todos os parceiros de uma variação. */
export async function setVariationPartners(
    variationId: number,
    partners: SetPartnerData[]
): Promise<ApiResponse<CatalogPartner[]>> {
    return apiRequest<CatalogPartner[]>(
        `catalog/${variationId}/partners`,
        { data: { partners } },
        'put'
    )
}

// ==================== PROFISSIONAIS ====================

export async function addCatalogProfessional(
    catalogId: number,
    data: CreateCatalogProfessionalData
): Promise<ApiResponse<CatalogProfessional>> {
    return apiRequest<CatalogProfessional>(`catalog/${catalogId}/professionals`, { data }, 'post')
}

export async function getCatalogProfessionals(catalogId: number): Promise<ApiResponse<CatalogProfessional[]>> {
    return apiRequest<CatalogProfessional[]>(`catalog/${catalogId}/professionals`, {})
}

export async function updateCatalogProfessional(
    catalogId: number,
    professionalId: number,
    data: UpdateCatalogProfessionalData
): Promise<ApiResponse<CatalogProfessional>> {
    return apiRequest<CatalogProfessional>(
        `catalog/${catalogId}/professionals/${professionalId}`,
        { data },
        'patch'
    )
}

/** Preço promocional do produto na filial; `promotionalPrice: null` encerra. */
export async function setCatalogPromotion(
    catalogId: number,
    data: {
        companyId: number
        promotionalPrice: number | null
        promotionalStart?: string
        promotionalEnd?: string
    }
): Promise<
    ApiResponse<{
        promotionalPrice: number | null
        promotionalStart: string | null
        promotionalEnd: string | null
    }>
> {
    return apiRequest(`catalog/${catalogId}/promotion`, { data }, 'patch')
}

export async function addCatalogVariation(
    catalogId: number,
    data: CreateCatalogVariationData
): Promise<ApiResponse<CatalogVariation>> {
    return apiRequest<CatalogVariation>(`catalog/${catalogId}/variations`, { data }, 'post')
}

/**
 * `scheduling`: cada variação só com os parceiros da filial de quem agenda (ver
 * `getCatalog`). Sem ele, os de todas as filiais (o editor do catálogo precisa).
 */
export async function getCatalogVariations(
    catalogId: number,
    options?: { scheduling?: boolean }
): Promise<ApiResponse<CatalogVariation[]>> {
    const query = options?.scheduling ? '?scope=scheduling' : ''
    return apiRequest<CatalogVariation[]>(`catalog/${catalogId}/variations${query}`, {})
}

export async function updateCatalogVariation(
    catalogId: number,
    variationId: number,
    data: UpdateCatalogVariationData
): Promise<ApiResponse<CatalogVariation>> {
    return apiRequest<CatalogVariation>(`catalog/${catalogId}/variations/${variationId}`, { data }, 'patch')
}

export async function removeCatalogVariation(
    catalogId: number,
    variationId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${catalogId}/variations/${variationId}`, {}, 'delete')
}

export async function removeCatalogProfessional(
    catalogId: number,
    professionalId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${catalogId}/professionals/${professionalId}`, {}, 'delete')
}

// ==================== HOSPITAIS ====================
// O vínculo item↔hospital NÃO tem preço (apenas associativo). Serve para atrelar
// o hospital a itens do catálogo a partir da aba "Catálogo do Hospital".

export interface CatalogHospitalLink {
    id: number
    catalogId: number
    hospitalId: number
    hospital: {
        id: number
        name: string
        tradingName?: string | null
        cnpj?: string | null
        city?: string | null
        state?: string | null
    }
    createdAt: string
}

export async function addCatalogHospital(
    catalogId: number,
    hospitalId: number
): Promise<ApiResponse<CatalogHospitalLink>> {
    return apiRequest<CatalogHospitalLink>(`catalog/${catalogId}/hospitals`, { data: { hospitalId } }, 'post')
}

export async function removeCatalogHospital(
    catalogId: number,
    hospitalId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${catalogId}/hospitals/${hospitalId}`, {}, 'delete')
}

// ==================== MATERIAIS MÉDICOS ====================

export async function addCatalogMedicalSupply(
    catalogId: number,
    data: { medicalSupplyId: number; quantity?: number; unitPrice?: number; notes?: string }
): Promise<ApiResponse<CatalogMedicalSupply>> {
    return apiRequest<CatalogMedicalSupply>(`catalog/${catalogId}/medical-supplies`, { data }, 'post')
}

export async function getCatalogMedicalSupplies(catalogId: number): Promise<ApiResponse<CatalogMedicalSupply[]>> {
    return apiRequest<CatalogMedicalSupply[]>(`catalog/${catalogId}/medical-supplies`, {})
}

export async function updateCatalogMedicalSupply(
    catalogId: number,
    medicalSupplyId: number,
    data: { quantity?: number; unitPrice?: number; notes?: string }
): Promise<ApiResponse<CatalogMedicalSupply>> {
    return apiRequest<CatalogMedicalSupply>(`catalog/${catalogId}/medical-supplies/${medicalSupplyId}`, { data }, 'patch')
}

export async function removeCatalogMedicalSupply(
    catalogId: number,
    medicalSupplyId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${catalogId}/medical-supplies/${medicalSupplyId}`, {}, 'delete')
}

// ==================== EXAMES RELACIONADOS ====================

export interface CatalogExam {
    id: number
    catalogId: number
    examCatalogId: number
    examCatalog: {
        id: number
        name: string
        category: CatalogCategory
        price: number
        sellingPrice?: number | null
        catalogType?: { id: number; name: string } | null
    }
    quantity: number
    complimentary: boolean
    notes?: string | null
    createdAt: string
    updatedAt: string
}

export async function addCatalogExam(
    catalogId: number,
    data: { examCatalogId: number; quantity?: number; complimentary?: boolean; notes?: string }
): Promise<ApiResponse<CatalogExam>> {
    return apiRequest<CatalogExam>(`catalog/${catalogId}/exams`, { data }, 'post')
}

export async function getCatalogExams(catalogId: number): Promise<ApiResponse<CatalogExam[]>> {
    return apiRequest<CatalogExam[]>(`catalog/${catalogId}/exams`, {})
}

export async function updateCatalogExam(
    catalogId: number,
    examCatalogId: number,
    data: { quantity?: number; complimentary?: boolean; notes?: string }
): Promise<ApiResponse<CatalogExam>> {
    return apiRequest<CatalogExam>(`catalog/${catalogId}/exams/${examCatalogId}`, { data }, 'patch')
}

export async function removeCatalogExam(
    catalogId: number,
    examCatalogId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${catalogId}/exams/${examCatalogId}`, {}, 'delete')
}

// ==================== COMPONENTES DE PACOTE ====================

/**
 * Componente de pacote. Serviço e mercadoria vêm ambos em `itemId`/`item` — os dois são
 * item de catálogo desde a unificação; quem diz o que é a linha é `kind`. Produto só
 * existe dentro de pacote. Ver docs/catalog-package-products.md §4-C.
 */
/** O que é a linha do componente. Ver docs/catalog-package-pricing.md §6.2. */
export type ComponentKind =
    | 'ITEM'
    | 'PRODUCT'
    | 'SUPPLY'
    | 'HOSPITAL'
    | 'ANESTHETIST'

/** Rótulo de quem recebe pelo componente. */
export const PAYOUT_RECIPIENT_LABELS: Record<
    'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER',
    string
> = {
    SUPPLIER: 'Fornecedor',
    HOSPITAL: 'Hospital',
    PROFESSIONAL: 'Profissional',
    PARTNER: 'Parceiro',
}

export const COMPONENT_KIND_LABELS: Record<ComponentKind, string> = {
    ITEM: 'Item',
    PRODUCT: 'Produto',
    SUPPLY: 'Material',
    HOSPITAL: 'Hospital',
    ANESTHETIST: 'Anestesista',
}

export interface CatalogPackageItem {
    id: number
    packageId: number
    kind?: ComponentKind
    medicalSupplyId?: number | null
    medicalSupply?: {
        id: number
        name: string
        type?: string
        unit?: string | null
        costPrice?: number | null
        supplierId?: number | null
        supplier?: { id: number; name: string } | null
    } | null
    /**
     * Custo unitário do componente (material, hospital, anestesista), em centavos.
     * ⚠️ É CUSTO: não soma ao preço do pacote, que é fechado.
     */
    amount?: number | null
    /** Quem recebe: SUPPLIER|HOSPITAL (material), HOSPITAL, ou HOSPITAL|PROFESSIONAL|PARTNER (anestesista). */
    recipientKind?: 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    recipientId?: number | null
    itemId?: number | null
    item?: {
        id: number
        name: string
        category: CatalogCategory
        tussCode?: string | null
        companyPrices?: CatalogCompanyPrice[]
        /**
         * Parceiros que executam ESTE item, com o custo do vínculo. É a lista que filtra o
         * seletor de parceiro do componente — só quem faz aquele item aparece — e é de onde
         * sai o valor do repasse do parceiro (não é um valor digitado).
         */
        partners?: {
            partnerId: number
            price?: number | null
            active?: boolean | null
            partner?: { id: number; name: string; tradingName?: string | null } | null
        }[]
        catalogType?: { id: number; name: string } | null
        /** Fornecedor do cadastro — quem recebe pelo componente-PRODUTO. */
        supplierId?: number | null
        supplier?: { id: number; name: string } | null
        productDetails?: { unit?: string | null } | null
        companyStocks?: { companyId: number; stockQuantity?: number | null }[]
    } | null
    quantity: number
    complimentary: boolean
    sortOrder: number
    notes?: string | null
    /**
     * Quem EXECUTA este componente. Só faz sentido em componente-item: produto não tem
     * parceiro nem profissional — tem fornecedor, que vem do cadastro do produto.
     *
     * O parceiro recebe o custo do vínculo (`item.partners`); o profissional recebe 100% de
     * `professionalAmount` (em centavos). Ver docs/catalog-package-pricing.md.
     */
    partnerId?: number | null
    partner?: { id: number; name: string; tradingName?: string | null } | null
    professionalId?: number | null
    professional?: { id: number; name: string } | null
    professionalAmount?: number | null
    createdAt: string
    updatedAt: string
}

export async function addCatalogPackageItem(
    packageId: number,
    data: {
        /** Item de catálogo — serviço (kind ITEM) ou mercadoria (kind PRODUCT). */
        itemId?: number
        kind?: ComponentKind
        medicalSupplyId?: number
        /** Custo unitário (material, hospital, anestesista), em centavos. */
        amount?: number
        recipientKind?: 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER'
        recipientId?: number
        quantity?: number
        complimentary?: boolean
        sortOrder?: number
        notes?: string
        /** Execução do componente (só item). Ver `CatalogPackageItem`. */
        partnerId?: number
        professionalId?: number
        /** Centavos. Vai 100% para o profissional. */
        professionalAmount?: number
    }
): Promise<ApiResponse<CatalogPackageItem>> {
    return apiRequest<CatalogPackageItem>(`catalog/${packageId}/package-items`, { data }, 'post')
}

export async function getCatalogPackageItems(packageId: number): Promise<ApiResponse<CatalogPackageItem[]>> {
    return apiRequest<CatalogPackageItem[]>(`catalog/${packageId}/package-items`, {})
}

/**
 * ⚠️ `componentId` é o id da LINHA do vínculo, não o do item de catálogo. Desde que um
 * componente pode ser item OU produto, `itemId` deixou de identificá-lo.
 */
export async function updateCatalogPackageItem(
    packageId: number,
    componentId: number,
    data: {
        quantity?: number
        complimentary?: boolean
        sortOrder?: number
        notes?: string
        amount?: number | null
        recipientKind?: 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
        recipientId?: number | null
        /** Execução do componente (só item; `null` limpa). Ver `CatalogPackageItem`. */
        partnerId?: number | null
        professionalId?: number | null
        /** Centavos. Vai 100% para o profissional. */
        professionalAmount?: number | null
    }
): Promise<ApiResponse<CatalogPackageItem>> {
    return apiRequest<CatalogPackageItem>(`catalog/${packageId}/package-items/${componentId}`, { data }, 'patch')
}

/**
 * Custo do pacote NAQUELA filial, composto pelos componentes: custo do parceiro de cada
 * componente + valor do profissional + custo do produto. Cortesia ENTRA no custo (o repasse
 * é devido de qualquer forma). É o que trava o campo "custo" do pacote na tela — custo
 * digitado à mão numa venda de pacote é chute. Ver docs/catalog-package-pricing.md §5.
 */
export async function getCatalogPackageCost(
    packageId: number,
    companyId?: number
): Promise<ApiResponse<{ cost: number }>> {
    return apiRequest<{ cost: number }>(
        `catalog/${packageId}/package-cost`,
        companyId ? { params: { companyId } } : {}
    )
}

/** `componentId` é o id da LINHA do vínculo (ver `updateCatalogPackageItem`). */
export async function removeCatalogPackageItem(
    packageId: number,
    componentId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog/${packageId}/package-items/${componentId}`, {}, 'delete')
}

/** Converte um combo legado (EXAM sem TUSS) em PACKAGE (master). dryRun simula. */
export async function convertComboToPackage(data: {
    catalogId: number
    components: { itemId?: number; tussCode?: string; quantity?: number; complimentary?: boolean }[]
    dryRun?: boolean
}): Promise<ApiResponse<any>> {
    return apiRequest<any>('catalog/convert-combo-to-package', { data }, 'post')
}

// ==================== TIPOS ====================

export interface CatalogType {
    id: number
    name: string
    category: CatalogCategory
    organizationId: number
    partnerId?: number | null
    createdAt: string
    updatedAt: string
}

export async function getCatalogTypesByOrganization(
    organizationId: number,
    params?: { partnerId?: number; category?: CatalogCategory; page?: number; limit?: number; search?: string }
): Promise<ApiResponse<CatalogType[]>> {
    const queryParams: Record<string, any> = {}
    if (params?.partnerId) queryParams.partnerId = params.partnerId
    if (params?.category) queryParams.category = params.category
    if (params?.page) queryParams.page = params.page
    if (params?.limit) queryParams.limit = params.limit
    if (params?.search) queryParams.search = params.search
    return apiRequest<CatalogType[]>(`catalog-type/organization/${organizationId}`, { params: queryParams })
}

export async function createCatalogType(data: {
    name: string
    category: CatalogCategory
    organizationId: number
    partnerId?: number
}): Promise<ApiResponse<CatalogType>> {
    return apiRequest<CatalogType>('catalog-type', { data }, 'post')
}

export async function updateCatalogType(
    id: number,
    data: Partial<{ name: string; category: CatalogCategory }>
): Promise<ApiResponse<CatalogType>> {
    return apiRequest<CatalogType>(`catalog-type/${id}`, { data }, 'patch')
}

export async function deleteCatalogType(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`catalog-type/${id}`, {}, 'delete')
}

// ==================== UTILIDADES ====================

export interface PartnerComparisonPdfPayload {
    items: { catalogId: number; quantity: number; complimentary?: boolean }[]
    serviceTypeLabel?: string
    clientId?: number
}

export async function openPartnerComparisonPdf(
    payload: PartnerComparisonPdfPayload,
): Promise<void> {
    const client = getClient()
    const response = await client.post('catalog/partner-comparison-pdf', payload, {
        responseType: 'blob',
    })
    downloadPdf(response.data, `cotacao-parceiros-${Date.now()}.pdf`)
}

export const DEFAULT_DURATIONS = [
    { label: '15 minutos', value: 15 },
    { label: '30 minutos', value: 30 },
    { label: '45 minutos', value: 45 },
    { label: '1 hora', value: 60 },
    { label: '1h 30min', value: 90 },
    { label: '2 horas', value: 120 },
    { label: '3 horas', value: 180 },
    { label: '4 horas', value: 240 },
]

// ==================== MÍDIA (fotos e vídeos do item) ====================

export type CatalogMediaKind = 'IMAGE' | 'VIDEO'
export type CatalogMediaProvider = 'UPLOAD' | 'YOUTUBE'

export interface CatalogMedia {
    id: number
    catalogId: number
    kind: CatalogMediaKind
    provider: CatalogMediaProvider
    /** Preenchida quando `provider = UPLOAD`. */
    url?: string | null
    /** Preenchido quando `provider = YOUTUBE`. */
    videoId?: string | null
    poster?: string | null
    title?: string | null
    sortOrder: number
}

/** Espelham os limites do backend (`catalog-media.service.ts`). */
export const CATALOG_MEDIA_MAX_IMAGES = 10
export const CATALOG_MEDIA_MAX_UPLOADED_VIDEOS = 5

export async function getCatalogMedia(
    catalogId: number,
): Promise<ApiResponse<CatalogMedia[]>> {
    return apiRequest<CatalogMedia[]>(`catalog/${catalogId}/media`, {})
}

export interface CatalogMediaUploadIntent {
    name: string
    kind: CatalogMediaKind
    objectKey: string
    /** URL pública final, já conhecida antes do envio. */
    url: string
    /** URL pré-assinada onde o navegador faz o PUT. */
    uploadUrl: string
}

/**
 * Passo 1: autoriza o envio. O backend valida quantidade, tamanho por arquivo
 * (do plano) e cota da organização ANTES de qualquer byte sair daqui.
 */
export async function createCatalogMediaIntent(
    catalogId: number,
    files: { name: string; mimeType: string; sizeBytes: number }[],
): Promise<ApiResponse<CatalogMediaUploadIntent[]>> {
    return apiRequest<CatalogMediaUploadIntent[]>(
        `catalog/${catalogId}/media/upload-intent`,
        { data: { files } },
        'post',
    )
}

/** Passo 3: avisa que os objetos foram gravados e anexa as mídias ao item. */
export async function confirmCatalogMedia(
    catalogId: number,
    objectKeys: string[],
): Promise<ApiResponse<CatalogMedia[]>> {
    return apiRequest<CatalogMedia[]>(
        `catalog/${catalogId}/media/confirm`,
        { data: { objectKeys } },
        'post',
    )
}

export async function addCatalogYoutubeMedia(
    catalogId: number,
    video: string,
    title?: string,
): Promise<ApiResponse<CatalogMedia>> {
    return apiRequest<CatalogMedia>(
        `catalog/${catalogId}/media/youtube`,
        { data: { video, title } },
        'post',
    )
}

export async function updateCatalogMediaTitle(
    catalogId: number,
    mediaId: number,
    title?: string,
): Promise<ApiResponse<CatalogMedia>> {
    return apiRequest<CatalogMedia>(
        `catalog/${catalogId}/media/${mediaId}`,
        { data: { title } },
        'patch',
    )
}

export async function reorderCatalogMedia(
    catalogId: number,
    ids: number[],
): Promise<ApiResponse<CatalogMedia[]>> {
    return apiRequest<CatalogMedia[]>(
        `catalog/${catalogId}/media/reorder`,
        { data: { ids } },
        'patch',
    )
}

export async function removeCatalogMedia(
    catalogId: number,
    mediaId: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `catalog/${catalogId}/media/${mediaId}`,
        {},
        'delete',
    )
}
